import React, { useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Popover } from 'antd'
import { Filter } from 'lucide-react'
import {
  useOptionalBranchSelectionContext,
  BranchSelectionBanner,
} from '../../../../features/branchSelection'
import { useLabelContext } from '../../../../context/label'
import { useOptionalLabelFilterContext, LabelFilterUI } from '../../../../features/labelFilter'
import { useOptionalReportSidebar } from '../../Sidebars/ReportSidebar/useReportSidebar'
import { useReadOnlyContext } from '../../../../context/readOnly'
import { useFeatureAccessContext } from '../../../../context/featureAccess'
import { useProfileContext } from '../../../../context/profile'
import { useCharacterModal } from '../../../../context/CharacterModalContext'
import { HEADER_FEATURE_FLAGS } from '../../../../constants/mindmapConfig'
import { ShareModal } from '../ShareModal'
import { CharacterSelectionModal } from '../../Modals/CharacterSelectionModal'
import { ZoomSlider } from '../../Controls/ZoomSlider'
import { NodeSearch } from '../../Controls/NodeSearch'
import {
  HeaderButtonsContainer,
  HeaderButtonsGroup,
  LeftControlsGroup,
  MobileTopRow,
  StyledButtonWrapper,
  ButtonText,
} from '../styles/MindMapHeaderStyles'
import { useSaveStatus, useResearchSummary } from './hooks'
import {
  MobileTooltip,
  AddNodePopover,
  ProjectSettingsMenu,
  SuperpowersMenu,
  SaveStatusDisplay,
  TeamButton,
  ResearchSplitButton,
} from './components'
import type { MindMapHeaderProps } from './MindMapHeader.types'

export const MindMapHeader: React.FC<MindMapHeaderProps> = ({
  isEmpty = false,
  showResearchButton = false,
  onAddNode,
  mindmapId,
  mindmapTitle: mindmapTitleProp = '',
  mindmapImageStyle: mindmapImageStyleProp = null,
  onMindmapTitleChange,
  onMindmapImageStyleChange,
  isSaving: isSavingProp = false,
  lastSaved: lastSavedProp,
  selectedFilterLabels: selectedFilterLabelsProp,
  onFilterLabelsChange: onFilterLabelsChangeProp,
}) => {
  const { labels: projectLabels } = useLabelContext()
  const { profile } = useProfileContext()
  const reportSidebar = useOptionalReportSidebar()
  const { isReadOnly } = useReadOnlyContext()
  const { isFeatureEnabled } = useFeatureAccessContext()
  const characterModal = useCharacterModal()
  const branchSelection = useOptionalBranchSelectionContext()
  const navigate = useNavigate()
  const { id: currentMindmapId } = useParams<{ id: string }>()
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)

  const { items: researchItems, count: researchCount } = useResearchSummary(currentMindmapId)

  const handleValidateConfirm = useCallback(
    (selectedIds: Set<string>): void => {
      if (!currentMindmapId) return
      const key = `validate-selection-${currentMindmapId}`
      sessionStorage.setItem(key, JSON.stringify([...selectedIds]))
      void navigate(`/mindmap/${currentMindmapId}/validate`)
    },
    [currentMindmapId, navigate]
  )

  const handleStartResearch = useCallback(() => {
    branchSelection?.activate({
      title: 'What do you want to validate?',
      subtitle: 'Pick the needs and ideas you want to test',
      onConfirm: handleValidateConfirm,
    })
  }, [branchSelection, handleValidateConfirm])

  const isSuperAdmin = profile?.role === 'super_admin'

  // Save status (context-first, falls back to props)
  const { isSaving, showIndicator } = useSaveStatus(isSavingProp, lastSavedProp)

  // Label filter (context-first, falls back to props)
  const labelFilterContext = useOptionalLabelFilterContext()
  const selectedFilterLabels =
    labelFilterContext?.selectedFilterLabels ?? selectedFilterLabelsProp ?? []
  const onFilterLabelsChange =
    labelFilterContext?.setSelectedFilterLabels ?? onFilterLabelsChangeProp ?? (() => {})
  const hasActiveFilter = selectedFilterLabels.length > 0

  if (isEmpty) return null

  if (branchSelection?.isActive) {
    return (
      <>
        <BranchSelectionBanner
          categoryCounts={branchSelection.selectedCategoryCounts}
          title={branchSelection.bannerTitle}
          subtitle={branchSelection.bannerSubtitle}
          onConfirm={branchSelection.confirmSelection}
          onCancel={branchSelection.deactivate}
        />
        <HeaderButtonsContainer>
          <LeftControlsGroup>
            <ZoomSlider />
            <NodeSearch />
          </LeftControlsGroup>
        </HeaderButtonsContainer>
      </>
    )
  }

  if (isReadOnly) {
    return (
      <HeaderButtonsContainer>
        <LeftControlsGroup>
          <ZoomSlider />
          <NodeSearch />
        </LeftControlsGroup>
      </HeaderButtonsContainer>
    )
  }

  return (
    <>
      <HeaderButtonsContainer>
        {/* Left side: zoom slider, search, add node */}
        <LeftControlsGroup>
          <ZoomSlider />
          <NodeSearch />
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Feature flag, kept for future use */}
          {HEADER_FEATURE_FLAGS.showAddNodeButton && <AddNodePopover onAddNode={onAddNode} />}
        </LeftControlsGroup>

        {/* Right side: action buttons */}
        <HeaderButtonsGroup>
          {isFeatureEnabled('labels') && (
            <MobileTooltip
              title={`Filter${hasActiveFilter ? ` (${selectedFilterLabels.length})` : ''}`}
            >
              <Popover
                content={
                  <LabelFilterUI
                    variant="popover"
                    selectedLabels={selectedFilterLabels}
                    onLabelsChange={onFilterLabelsChange}
                    availableLabels={projectLabels}
                  />
                }
                trigger="click"
                placement="bottomRight"
                open={filterPopoverOpen}
                onOpenChange={setFilterPopoverOpen}
                styles={{ body: { background: 'var(--color-bg-elevated)', padding: 0 } }}
              >
                <StyledButtonWrapper>
                  <Button
                    type={hasActiveFilter ? 'primary' : 'default'}
                    icon={<Filter size={16} />}
                  >
                    <ButtonText>
                      Filter{hasActiveFilter && ` (${selectedFilterLabels.length})`}
                    </ButtonText>
                  </Button>
                </StyledButtonWrapper>
              </Popover>
            </MobileTooltip>
          )}

          <ResearchSplitButton
            mindmapId={currentMindmapId}
            items={researchItems}
            count={researchCount}
            hasValidatableContent={showResearchButton}
            onStartResearch={handleStartResearch}
          />

          {isFeatureEnabled('simulated_stakeholders') && <TeamButton />}

          {isSuperAdmin && mindmapId && <SuperpowersMenu />}

          <MobileTopRow>
            <SaveStatusDisplay isSaving={isSaving} showIndicator={showIndicator} />
            {mindmapId && (
              <ProjectSettingsMenu
                mindmapId={mindmapId}
                mindmapTitle={mindmapTitleProp}
                mindmapImageStyle={mindmapImageStyleProp}
                onTitleChange={onMindmapTitleChange}
                onImageStyleChange={onMindmapImageStyleChange}
                onShareClick={() => setShowShareModal(true)}
                onExportClick={() => {
                  if (reportSidebar?.isOpen) {
                    reportSidebar.close()
                  } else {
                    branchSelection?.activate({
                      title: 'Select branches to export',
                      subtitle: 'Choose which branches to include in the report',
                      onConfirm: (selectedIds) => reportSidebar?.open(selectedIds),
                    })
                  }
                }}
              />
            )}
          </MobileTopRow>
        </HeaderButtonsGroup>
      </HeaderButtonsContainer>

      {/* Modals rendered at top level — they can be triggered from outside the header
          (e.g. CharacterModal from StakeholderCTAList) */}

      <CharacterSelectionModal
        isVisible={characterModal.isOpen}
        onClose={characterModal.closeModal}
        initialSelectedTemplateId={characterModal.initialSelectedTemplateId}
      />

      {mindmapId && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          mindmapId={mindmapId}
        />
      )}
    </>
  )
}
